import { apiFetch } from "./api";
import { queryClient } from "./query-client";
import { tokenStorage } from "./storage-tokens";
import { useAuthStore } from "./stores/auth";
import {
  identifyUser,
  resetAnalytics,
  trackEvent,
  AnalyticsEvent,
} from "./analytics";
import { clearTransientStores } from "./clear-transient-stores";
import { getToltReferral } from "./tolt";

export type AuthUser = {
  id: number;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  profileCompleted: boolean;
};

export type SessionDto = {
  id: number;
  deviceName: string | null;
  browser: string | null;
  location: string | null;
  lastActiveAt: string;
  createdAt: string;
  isCurrent: boolean;
};

export type AuthOrg = {
  id: number;
  name: string;
};

export type AuthWorkspace = {
  id: number;
  name: string;
};

export type AuthResponse = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  organization: AuthOrg;
  workspace: AuthWorkspace;
};

export type NeedsProfile = {
  pendingToken: string;
  prefillName: string | null;
  prefillAvatarUrl: string | null;
};

export type AuthResult = AuthResponse | NeedsProfile;

export function isNeedsProfile(r: AuthResult): r is NeedsProfile {
  return "pendingToken" in r;
}

export async function requestMagicLink(
  email: string,
  inviteToken?: string,
  shareableLinkToken?: string,
  captchaToken?: string,
): Promise<void> {
  await apiFetch<void>("/auth/magic-link", {
    method: "POST",
    body: JSON.stringify({
      email,
      ...(inviteToken ? { inviteToken } : {}),
      ...(shareableLinkToken ? { shareableLinkToken } : {}),
      ...(captchaToken ? { captchaToken } : {}),
    }),
    auth: false,
  });
}

/**
 * Password login. Single call — a correct password mints the session outright,
 * with no OTP step. Only accounts carrying a password (migrated from the
 * previous TrulyInbox, or set via a reset) can use this; everyone else signs in
 * by magic link.
 */
export async function loginWithPassword(
  email: string,
  password: string,
  workspaceId?: number,
  rememberMe?: boolean,
  captchaToken?: string,
): Promise<AuthResult> {
  const result = await apiFetch<AuthResult>("/auth/login/password", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      ...(workspaceId ? { workspaceId } : {}),
      ...(rememberMe !== undefined ? { rememberMe } : {}),
      ...(captchaToken ? { captchaToken } : {}),
    }),
    auth: false,
  });

  // Same seam, same reasoning as `verifyMagicCode`: this is the only place that
  // both knows `rememberMe` and is guaranteed to run before any token reaches
  // storage. After the await so a failed login never flips the mode.
  if (rememberMe !== undefined) {
    tokenStorage.setPersistence(rememberMe ? "persistent" : "session");
  }

  return result;
}

/** Request a password reset link. Resolves identically whether or not the account exists. */
export async function requestPasswordReset(
  email: string,
  captchaToken?: string,
): Promise<void> {
  await apiFetch<{ message: string }>("/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email, ...(captchaToken ? { captchaToken } : {}) }),
    auth: false,
  });
}

/** Consume the emailed reset token and set a new password. Revokes every existing session. */
export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  await apiFetch<{ message: string }>("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ token, password }),
    auth: false,
  });
}

/**
 * Step 1 of email+password signup. Creates an UNVERIFIED account and emails a
 * code — no session is issued and the password cannot be used until the code is
 * verified, so this resolving does NOT mean the user is signed in.
 */
export async function signupWithPassword(
  email: string,
  password: string,
  captchaToken?: string,
): Promise<void> {
  await apiFetch<{ message: string }>("/auth/signup/password", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      ...(captchaToken ? { captchaToken } : {}),
    }),
    auth: false,
  });
}

/**
 * Step 2 of email+password signup. Proves the address, then returns
 * `NeedsProfile` so the caller lands on /complete-profile — the same screen
 * magic-link and OAuth signups use, which is what creates the org + workspace.
 */
export async function verifySignupOtp(
  email: string,
  code: string,
): Promise<AuthResult> {
  return apiFetch<AuthResult>("/auth/signup/password/verify", {
    method: "POST",
    body: JSON.stringify({ email, code }),
    auth: false,
  });
}

/** Re-send the signup code. Resolves identically whether or not one was sent. */
export async function resendSignupOtp(
  email: string,
  captchaToken?: string,
): Promise<void> {
  await apiFetch<{ message: string }>("/auth/signup/password/resend", {
    method: "POST",
    body: JSON.stringify({ email, ...(captchaToken ? { captchaToken } : {}) }),
    auth: false,
  });
}

export async function verifyMagicCode(
  email: string,
  code: string,
  // Seamless re-auth (TRU-301): the workspace the user was on at expiry, so the
  // fresh token targets it instead of falling back to their last workspace.
  workspaceId?: number,
  // "Keep me signed in". `undefined` means the caller expresses no preference
  // (the re-auth modal), which must leave this tab's existing mode untouched —
  // hence `!== undefined` rather than the truthy spread used above, which would
  // also swallow a deliberate `false`.
  rememberMe?: boolean,
): Promise<AuthResult> {
  const result = await apiFetch<AuthResult>("/auth/magic-link/verify", {
    method: "POST",
    body: JSON.stringify({
      email,
      code,
      ...(workspaceId ? { workspaceId } : {}),
      ...(rememberMe !== undefined ? { rememberMe } : {}),
    }),
    auth: false,
  });

  // The persistence mode is set here rather than in `storeAuth` because this is
  // the only seam that both knows `rememberMe` and is guaranteed to run before
  // any token reaches storage. `storeAuth` would also be "before the write",
  // but it has five other callers (OAuth exchange, magic-link callback,
  // complete-profile, accept-invite, re-auth modal) that would each have to
  // thread a flag they never receive.
  //
  // After the await, so a failed verify never flips the mode; before the
  // return, so the caller's `onVerified` → `storeAuth(result)` already writes
  // under it. A `NeedsProfile` result writes no tokens here — the mode simply
  // stays in sessionStorage across the /complete-profile navigation, so the
  // session created there honours the same choice.
  if (rememberMe !== undefined) {
    tokenStorage.setPersistence(rememberMe ? "persistent" : "session");
  }

  return result;
}

export async function exchangeOAuthToken(token: string): Promise<AuthResult> {
  return apiFetch<AuthResult>("/auth/oauth-exchange", {
    method: "POST",
    body: JSON.stringify({ token }),
    auth: false,
  });
}

export async function oneTapGoogleLogin(
  credential: string,
  shareableLinkToken?: string,
): Promise<AuthResult> {
  return apiFetch<AuthResult>("/auth/google/one-tap", {
    method: "POST",
    body: JSON.stringify({
      credential,
      ...(shareableLinkToken ? { shareableLinkToken } : {}),
    }),
    auth: false,
  });
}

export async function exchangeMagicLinkToken(
  token: string,
): Promise<AuthResult> {
  return apiFetch<AuthResult>(
    `/auth/magic-link/callback?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
      auth: false,
    },
  );
}

export async function completeProfile(
  pendingToken: string,
  name: string,
  timezone?: string,
): Promise<AuthResponse> {
  const referralId = getToltReferral();
  return apiFetch<AuthResponse>("/auth/complete-profile", {
    method: "POST",
    body: JSON.stringify({
      pendingToken,
      name,
      ...(timezone ? { timezone } : {}),
      // First-touch affiliate attribution; the API stores it on the NEW org only.
      ...(referralId ? { referralId } : {}),
    }),
    auth: false,
  });
}

// Public auth helpers delegate to the Zustand store. The store's `persist`
// middleware writes the same legacy localStorage keys (`access_token`,
// `refresh_token`, `user`, `org`, `workspace`) via a custom split adapter, so
// `lib/api.ts` and any pre-existing session continue to work unchanged.

export function storeAuth(auth: AuthResponse): void {
  useAuthStore.getState().setAuth(auth);
  identifyUser(auth.user);
  queryClient.clear();
}

export function clearAuth(): void {
  useAuthStore.getState().clear();
  resetAnalytics();
  clearTransientStores();
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return useAuthStore.getState().refreshToken;
}

export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false;
  return !!useAuthStore.getState().accessToken;
}

export async function logout(refreshToken: string): Promise<void> {
  await apiFetch<void>("/auth/logout", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });
}

export async function signOut(): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  try {
    if (refreshToken) await logout(refreshToken);
  } catch {
    // proceed even if the API call fails — local state must still clear
  }
  // Track before clearAuth's resetAnalytics() drops the identified user.
  trackEvent(AnalyticsEvent.SignedOut);
  clearAuth();
}

/**
 * Ends every session for the current user: revokes all OTHER sessions
 * server-side, then logs out this one. The revoke must succeed before the
 * local sign-out — if it fails it throws and the user stays signed in here,
 * rather than walking away believing their other devices are logged out.
 */
export async function signOutEverywhere(): Promise<void> {
  await revokeAllSessions();
  await signOut();
}

export async function switchWorkspace(id: number): Promise<AuthResponse> {
  return apiFetch<AuthResponse>(`/auth/workspace/${id}/switch`, {
    method: "POST",
  });
}

export async function getSessions(): Promise<SessionDto[]> {
  return apiFetch<SessionDto[]>("/auth/sessions");
}

export type GleapUserHash = { hash: string | null };

/** Gleap Identity Verification hash for the current user — see `lib/gleap.ts`. */
export async function getGleapUserHash(): Promise<GleapUserHash> {
  return apiFetch<GleapUserHash>("/auth/gleap-user-hash");
}

export type GleapAnonymousHash = {
  anonymousUserId: string | null;
  anonymousUserHash: string | null;
};

/** Gleap Identity Verification hash for a not-yet-logged-in visitor. */
export async function getAnonymousGleapHash(): Promise<GleapAnonymousHash> {
  return apiFetch<GleapAnonymousHash>("/auth/anonymous-user-hash", { auth: false });
}

export async function revokeSession(id: number): Promise<void> {
  await apiFetch<void>(`/auth/sessions/${id}`, { method: "DELETE" });
}

export async function revokeAllSessions(): Promise<void> {
  await apiFetch<void>("/auth/sessions", { method: "DELETE" });
}

export async function acceptInvite(token: string): Promise<AuthResult> {
  return apiFetch<AuthResult>(
    `/auth/accept-invite?token=${encodeURIComponent(token)}`,
    { auth: false },
  );
}

export async function acceptInviteLink(token: string): Promise<AuthResponse> {
  return apiFetch<AuthResponse>(
    `/auth/accept-invite-link?token=${encodeURIComponent(token)}`,
  );
}
